import { cva, type VariantProps } from 'class-variance-authority'
import { LucideIcon, X } from 'lucide-react'
import * as React from 'react'

import { cn } from 'src/utils/cn'

const badgeVariants = cva('inline-flex items-center space-x-2 rounded-full border transition-colors', {
  variants: {
    variant: {
      default: 'border-transparent bg-primary text-primary-foreground',
      secondary: 'border-secondary/80 bg-secondary/20 text-foreground',
      accent: 'border bg-accent',
      destructive: 'border-destructive/80 bg-destructive/20 text-destructive',
      outline: 'text-foreground',
      warning: 'border-[#FADEAD] bg-[#FCECCF] text-primary',
    },
    size: {
      default: 'px-3.5 py-1.5 font-medium',
      small: 'px-2 py-1 text-xs font-medium',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  icon?: LucideIcon
  text: string
  showCloseIcon?: boolean
  onClick?: () => void
}

function Badge({ className, variant, size, icon: Icon, text, showCloseIcon, onClick, ...props }: BadgeProps) {
  return (
    <div
      onClick={onClick}
      className={cn(badgeVariants({ variant, size }), className, { 'cursor-pointer': showCloseIcon })}
      {...props}
    >
      {Icon && <Icon className={`${size === 'small' ? 'size-4' : 'size-5'}`} />}
      <span>{text}</span>
      {showCloseIcon && (
        <button
          className={`ml-2 flex cursor-pointer items-center justify-center rounded-full bg-primary text-white ${
            size === 'small' ? 'size-3' : 'size-4'
          }`}
        >
          <X className={`${size === 'small' ? 'size-1.5' : 'size-2.5'}`} />
        </button>
      )}
    </div>
  )
}

export { Badge, badgeVariants }
